
import { defineComponent } from 'vue';

const MOBILE_BREAKPOINT = 600;
const TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 16;
let currentOpenTooltip: { close: () => void } | null = null;

export default defineComponent({
  name: 'ContentTooltip',

  props: {
    tooltipContent: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      justOpened: false,
      showAbove: true,
      pollFrameId: null as number | null,
    };
  },

  computed: {
    isMobile(): boolean {
      return (this.$vuetify?.breakpoint?.width ?? MOBILE_BREAKPOINT) < MOBILE_BREAKPOINT;
    },
    tooltipContentClass(): string {
      const base = 'content-tooltip__popup';
      return this.showAbove ? base : `${base} ${base}--bottom`;
    },
  },

  watch: {
    isOpen(newVal: boolean) {
      if (newVal) {
        if (currentOpenTooltip && currentOpenTooltip !== this) {
          currentOpenTooltip.close();
        }
        currentOpenTooltip = this;
        this.justOpened = true;
        setTimeout(() => {
          this.justOpened = false;
        }, 100);
        if (this.isMobile) {
          this.startPositionPolling();
        }
      } else {
        if (currentOpenTooltip === this) {
          currentOpenTooltip = null;
        }
        this.stopPositionPolling();
      }
    },
  },

  beforeDestroy() {
    this.stopPositionPolling();
  },

  methods: {
    getTriggerEl(): HTMLElement | null {
      return (this.$refs.trigger as HTMLElement) || (this.$el as HTMLElement) || null;
    },
    getTooltipContentEl(): HTMLElement | null {
      const tooltip = this.$refs.tooltip as any;
      const contentEl = tooltip?.$refs?.content as HTMLElement | undefined;
      if (contentEl?.classList?.contains('content-tooltip__popup')) {
        return contentEl;
      }
      return document.querySelector(
        '.v-tooltip__content.content-tooltip__popup.menuable__content__active',
      ) as HTMLElement | null;
    },
    updatePosition() {
      if (!this.isMobile) {
        this.showAbove = true;
        return;
      }
      const triggerEl = this.getTriggerEl();
      if (!triggerEl) return;
      const rect = triggerEl.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      this.showAbove = spaceAbove >= spaceBelow;
    },
    startPositionPolling() {
      this.stopPositionPolling();
      this.$nextTick(() => {
        this.repositionTooltip();
      });
      const poll = () => {
        if (!this.isOpen) {
          this.pollFrameId = null;
          return;
        }
        this.repositionTooltip();
        this.pollFrameId = requestAnimationFrame(poll);
      };
      this.pollFrameId = requestAnimationFrame(poll);
    },
    stopPositionPolling() {
      if (this.pollFrameId !== null) {
        cancelAnimationFrame(this.pollFrameId);
        this.pollFrameId = null;
      }
    },
    repositionTooltip() {
      const triggerEl = this.getTriggerEl();
      const contentEl = this.getTooltipContentEl();
      if (!triggerEl || !contentEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();

      const tooltipHeight = contentEl.offsetHeight;
      const tooltipWidth = contentEl.offsetWidth;

      const computeTop = (showAbove: boolean) =>
        showAbove
          ? triggerRect.top - tooltipHeight - TOOLTIP_GAP
          : triggerRect.bottom + TOOLTIP_GAP;

      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      let left = triggerCenter - tooltipWidth / 2;
      left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, window.innerWidth - tooltipWidth - VIEWPORT_PADDING),
      );

      let top = computeTop(this.showAbove);
      const topOverflow = top < VIEWPORT_PADDING;
      const bottomOverflow = top + tooltipHeight > window.innerHeight - VIEWPORT_PADDING;

      if (this.showAbove && topOverflow && !bottomOverflow) {
        this.showAbove = false;
        top = computeTop(this.showAbove);
      } else if (!this.showAbove && bottomOverflow && !topOverflow) {
        this.showAbove = true;
        top = computeTop(this.showAbove);
      }

      const tooltipBottom = top + tooltipHeight;
      if (tooltipBottom < 0 || top > window.innerHeight) {
        this.isOpen = false;
        return;
      }

      contentEl.style.setProperty('position', 'fixed', 'important');
      contentEl.style.setProperty('top', `${top}px`, 'important');
      contentEl.style.setProperty('left', `${left}px`, 'important');
      contentEl.classList.toggle('content-tooltip__popup--bottom', !this.showAbove);
    },
    close() {
      this.isOpen = false;
    },
    onClickOutside() {
      if (this.isMobile && !this.justOpened) {
        this.isOpen = false;
      }
    },
  },
});
